// @file Vue mixin that handle [Cmd + V / Ctrl + V] inside a surface to post stuff
import { trackEvent } from 'bits/analytics'
import { isUrl } from 'bits/url'
import { PostAttributes } from 'types'
import { Component, Vue } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import { loadPostContentHashString } from 'vuexstore/helpers/post'

function trackPastePostEvent(type: string): void {
  trackEvent('Posts', 'add', `Paste ${type}`)
}

function extractPostAttributesFromPastedText(text: string): PostAttributes {
  if (isUrl(text)) {
    trackPastePostEvent('Link')
    return { attachment: text }
  }

  const postAttributesFromHashString = loadPostContentHashString(text)
  if (postAttributesFromHashString) {
    trackPastePostEvent('Hash')
    return postAttributesFromHashString
  }

  trackPastePostEvent('Text')
  return { body: text }
}

function extractPostAttributesFromPastedFile(file: File): PostAttributes {
  trackPastePostEvent('File')
  return { file }
}

function isTargetAnInputField(eventTarget?: EventTarget | null): boolean {
  if (!eventTarget) return false
  const target = eventTarget as HTMLElement
  const tagName = target.tagName && target.tagName.toUpperCase()

  const isInputElement =
    ['TEXTAREA', 'INPUT', 'TRIX-EDITOR'].includes(tagName) ||
    target.contentEditable === 'true' ||
    target.isContentEditable
  // Trix keeps a copy of the editor in some sort of dom outside the current dom.
  const isLikelyTrixExternalDom = (): boolean => {
    return !document.documentElement.contains(target)
  }
  const isInTrixEditor = (): boolean => {
    const trixCollection = document.getElementsByTagName('trix-editor')
    for (let i = 0; i < trixCollection.length; i++) {
      const trixElement = trixCollection[i]
      if (trixElement.contains(target)) return true
    }
    return false
  }

  return isInputElement || isLikelyTrixExternalDom() || isInTrixEditor()
}

interface NonStandardClipboardEvent extends ClipboardEvent {
  originalTarget?: EventTarget
  explicitOriginalTarget?: EventTarget
}

function isEventOnAnInputField(event: NonStandardClipboardEvent): boolean {
  // Sometimes, target of pasting will be onto arbitrary #text target
  // In that case, we get originalTarget, or even explicitOriginalTarget
  return (
    isTargetAnInputField(event.target) ||
    isTargetAnInputField(event.originalTarget) ||
    isTargetAnInputField(event.explicitOriginalTarget)
  )
}

type CustomSave = (post: PostAttributes) => void
const postModule = namespace('post')
let isPasteToPostSubscribed = false
let customSave: CustomSave | null = null
@Component
export default class PasteToPostMixin extends Vue {
  @postModule.Action createPost

  processPasteToPostEvent(event: Event): void {
    const e = event as NonStandardClipboardEvent
    // Sometimes, we don't get e.target, especially in Firefox.
    // In that case, ignore.
    if (!e.target || !(e.target as HTMLElement).tagName) {
      return
    }
    // Sometimes we don't get clipboard data, especially in Safari.
    // In that case, ignore.
    if (!e.clipboardData || !e.clipboardData.items) {
      return
    }
    // Ignore if event is on an input field
    if (isEventOnAnInputField(e)) {
      return
    }

    // for of throws errors, hence the old-school loop
    for (let i = 0; i < e.clipboardData.items.length; i++) {
      const clipboardItem: DataTransferItem = e.clipboardData.items[i]
      const itemType = clipboardItem.type
      let attributes: PostAttributes | null = null
      if (itemType.includes('image')) {
        const file = clipboardItem.getAsFile()
        if (file) {
          attributes = extractPostAttributesFromPastedFile(file)
        }
      } else if (itemType === 'text/plain') {
        const text = e.clipboardData.getData('text')
        // if text is not null have at least 1 non-whitespace character
        if (text && /\S/.test(text)) {
          attributes = extractPostAttributesFromPastedText(text)
        }
      }
      if (attributes) {
        customSave ? customSave(attributes) : this.createPost({ attributes })
      }
    }
  }

  enablePasteToPost(customSaveMethod: CustomSave | null = null): void {
    if (isPasteToPostSubscribed) return
    customSave = customSaveMethod
    window.addEventListener('paste', this.processPasteToPostEvent.bind(this))
    isPasteToPostSubscribed = true
  }

  disablePasteToPost(): void {
    if (!isPasteToPostSubscribed) return
    customSave = null
    window.removeEventListener('paste', this.processPasteToPostEvent.bind(this))
    isPasteToPostSubscribed = false
  }
}

export { CustomSave }
